import React, { useState, useEffect } from 'react';
import { Smile } from 'lucide-react';
import { Button } from '../ui/button';
import { REACTION_TYPES, ReactionType } from '../../types/reactions';
import { useAuth } from '../../hooks/useAuth';
import { reactionsService } from '../../services/reactionsService';
import { employeeNamesService } from '../../services/employeeNames';
import { toast } from '../ui/use-toast';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import '../../styles/reactions.css';

interface ReactionButtonProps {
  goodThingId: string;
  className?: string;
}

export const ReactionButton: React.FC<ReactionButtonProps> = React.memo(({ goodThingId, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [userReaction, setUserReaction] = useState<ReactionType | null>(null);
  const [bounceEmoji, setBounceEmoji] = useState<ReactionType | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.email) return;

    // Check if user has already reacted
    const checkUserReaction = async () => {
      const userReaction = await reactionsService.getUserReaction(goodThingId, user.uid);
      if (userReaction) {
        setUserReaction(userReaction.reactionType);
      }
    };

    checkUserReaction();
  }, [goodThingId, user?.email, user?.uid]);

  const handleReaction = async (reactionType: ReactionType) => {
    if (!user?.email) return;

    // Optimistic update
    const previousReaction = userReaction;
    const isRemoving = userReaction === reactionType;
    
    // Update UI immediately
    if (isRemoving) {
      setUserReaction(null);
    } else {
      setUserReaction(reactionType);
      setBounceEmoji(reactionType);
      setTimeout(() => setBounceEmoji(null), 1000);
    }
    
    setOpen(false);

    try {
      const displayName = await employeeNamesService.getNameFromEmail(user.email);
      // Fallback to email if display name is not found, or just proceed. 
      // The service returns null if not found, but we need a string.
      const finalDisplayName = displayName || user.displayName || user.email.split('@')[0];

      if (isRemoving) {
        await reactionsService.removeReaction(goodThingId, user.uid);
      } else {
        await reactionsService.addReaction(goodThingId, user.uid, finalDisplayName, reactionType);
      }
    } catch (error) {
      console.error('Error handling reaction:', error);
      // Revert on error
      setUserReaction(previousReaction);
      toast({
        title: "Error",
        description: "Failed to update reaction. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`h-10 w-10 p-0 ${className}`}
        >
          <Smile className={`h-5 w-5 ${userReaction ? 'text-pink-500' : 'text-gray-500'}`} />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-auto p-2 bg-white dark:bg-gray-800 border dark:border-gray-700 shadow-xl rounded-full z-[10001] reaction-popover-content pointer-events-auto" 
        side="top"
      >
        <div className="flex items-center gap-1">
          {REACTION_TYPES.map((type) => (
            <button
              key={type}
              className={`text-2xl hover:scale-125 transition-transform p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer ${
                userReaction === type ? 'bg-pink-50 dark:bg-pink-900/30' : ''
              } ${bounceEmoji === type ? 'animate-bounce' : ''}`}
              onClick={() => handleReaction(type)}
            >
              {type}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
});

ReactionButton.displayName = 'ReactionButton';

export default ReactionButton;